import React from 'react';
import { C } from '../../../shared/components/dashboard';
import { useI18n } from '../../../../i18n';
import type { Proveedor, NewProviderForm } from './admin-ia-shared';
import {
  INPUT_STYLE,
  LABEL_STYLE,
  MODEL_PILL,
  SECTION_CARD,
  SECTION_TITLE,
  SDK_NATIVE_PROVIDER_TYPES,
} from './admin-ia-shared';
import { InfoTooltip } from '@/components/ui/InfoTooltip';

interface AdminIAProvidersProps {
  providers: Proveedor[];
  showNewProvider: boolean;
  newProvider: NewProviderForm;
  onSetShowNewProvider: (v: boolean) => void;
  onSetNewProvider: React.Dispatch<React.SetStateAction<NewProviderForm>>;
  onUpdateProviderLocal: (id: string, patch: Partial<Proveedor>) => void;
  onSaveProviderKey: (prov: Proveedor) => void;
  onEditKey: (provId: string) => void;
  onCancelEditKey: (provId: string) => void;
  onToggleProviderActive: (prov: Proveedor) => void;
  onTestKey: (id: string) => void;
  onCreateProvider: () => void;
  providerTypeLabel: (tipo: string) => string;
}

export function AdminIAProviders({
  providers,
  showNewProvider,
  newProvider,
  onSetShowNewProvider,
  onSetNewProvider,
  onUpdateProviderLocal,
  onSaveProviderKey,
  onEditKey,
  onCancelEditKey,
  onToggleProviderActive,
  onTestKey,
  onCreateProvider,
  providerTypeLabel,
}: AdminIAProvidersProps) {
  const { t } = useI18n();
  return (
    <div style={SECTION_CARD}>
      <h3 style={{ ...SECTION_TITLE, display: 'inline-flex', alignItems: 'center' }}>
        {t.admin.ia.proveedores.titulo}
        <InfoTooltip title={t.admin.ia.proveedores.titulo} text={t.admin.ia.proveedores.ayuda} />
      </h3>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
          gap: 14,
        }}
      >
        {providers.map((prov) => (
          <div
            key={prov.id}
            style={{
              border: `1px solid ${C.grisMedio}`,
              borderRadius: 8,
              padding: '14px 16px',
              background: '#fff',
            }}
          >
            {/* Provider name + badge */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <span
                style={{
                  fontWeight: 600,
                  fontSize: 13,
                  color: C.azul1,
                  fontFamily: 'Montserrat, sans-serif',
                }}
              >
                {prov.nombre}
              </span>
              <span
                style={{
                  fontSize: 9,
                  fontWeight: 600,
                  borderRadius: 10,
                  padding: '2px 8px',
                  background: prov.activo ? '#DCFCE7' : '#f3f4f6',
                  color: prov.activo ? '#15803D' : '#9ca3af',
                }}
              >
                {prov.activo ? 'Activo' : 'Inactivo'}
              </span>
            </div>

            {/* Type + SDK/OpenAI badge */}
            <div
              style={{
                fontSize: 11,
                color: C.grisOscuro,
                marginBottom: 8,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                flexWrap: 'wrap',
              }}
            >
              <span>Tipo: {providerTypeLabel(prov.tipo)}</span>
              <span
                style={{
                  fontSize: 9,
                  fontWeight: 600,
                  borderRadius: 10,
                  padding: '1px 6px',
                  background: SDK_NATIVE_PROVIDER_TYPES.has(prov.tipo.toLowerCase())
                    ? '#EEF2FF'
                    : '#ECFDF5',
                  color: SDK_NATIVE_PROVIDER_TYPES.has(prov.tipo.toLowerCase())
                    ? '#4338CA'
                    : '#059669',
                }}
              >
                {SDK_NATIVE_PROVIDER_TYPES.has(prov.tipo.toLowerCase())
                  ? 'SDK propio'
                  : 'OpenAI-compatible'}
              </span>
              {prov.prioridad !== undefined && (
                <span
                  style={{
                    fontSize: 9,
                    fontWeight: 600,
                    borderRadius: 10,
                    padding: '1px 6px',
                    background: '#FEF3C7',
                    color: '#92400E',
                  }}
                >
                  Prioridad: {prov.prioridad}
                </span>
              )}
            </div>

            {/* baseUrl / modelo / prioridad editables (solo OpenAI-compatible) */}
            {!SDK_NATIVE_PROVIDER_TYPES.has(prov.tipo.toLowerCase()) && (
              <div style={{ marginBottom: 8, display: 'grid', gap: 4 }}>
                <input
                  type="text"
                  placeholder="baseUrl (https://api.proveedor.com/v1)"
                  style={{ ...INPUT_STYLE, fontSize: 10, padding: '3px 6px' }}
                  value={prov.baseUrl ?? ''}
                  onChange={(e) => onUpdateProviderLocal(prov.id, { baseUrl: e.target.value })}
                />
                <input
                  type="text"
                  placeholder="modelo predeterminado"
                  style={{ ...INPUT_STYLE, fontSize: 10, padding: '3px 6px' }}
                  value={prov.modeloPredeterminado ?? ''}
                  onChange={(e) =>
                    onUpdateProviderLocal(prov.id, { modeloPredeterminado: e.target.value })
                  }
                />
              </div>
            )}
            <input
              type="number"
              min={1}
              max={99}
              placeholder="prioridad (1=alta, 99=baja)"
              style={{
                ...INPUT_STYLE,
                fontSize: 10,
                padding: '3px 6px',
                marginBottom: 8,
                width: 120,
              }}
              value={prov.prioridad ?? ''}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10);
                onUpdateProviderLocal(prov.id, { prioridad: isNaN(v) ? undefined : v });
              }}
            />

            {/* Model pills */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 10 }}>
              {prov.modelos.map((m) => (
                <span key={m.nombre} style={MODEL_PILL}>
                  {m.nombre}
                </span>
              ))}
            </div>

            {/* API Key */}
            <div style={{ marginBottom: 8 }}>
              {prov.editKey ? (
                <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                  <input
                    type="text"
                    style={{ ...INPUT_STYLE, flex: 1, fontSize: 11, padding: '4px 8px' }}
                    placeholder="sk-..."
                    value={prov.keyDraft}
                    onChange={(e) => onUpdateProviderLocal(prov.id, { keyDraft: e.target.value })}
                  />
                  <button
                    onClick={() => onSaveProviderKey(prov)}
                    disabled={prov.testing}
                    style={{
                      padding: '4px 8px',
                      background: prov.testing ? '#6b7280' : '#15803D',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 4,
                      fontSize: 10,
                      fontWeight: 600,
                      cursor: prov.testing ? 'default' : 'pointer',
                      fontFamily: 'inherit',
                      opacity: prov.testing ? 0.7 : 1,
                    }}
                  >
                    {prov.testing ? 'Probando...' : 'OK'}
                  </button>
                  <button
                    onClick={() => onCancelEditKey(prov.id)}
                    style={{
                      padding: '4px 8px',
                      background: '#6b7280',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 4,
                      fontSize: 10,
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    Cancelar
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                  <code
                    style={{
                      fontSize: 10,
                      color: '#374151',
                      background: '#f9fafb',
                      padding: '2px 6px',
                      borderRadius: 4,
                      fontFamily: 'monospace',
                      flex: 1,
                    }}
                  >
                    {prov.keySource === 'env'
                      ? 'Configurada (entorno)'
                      : prov.keySource === 'bd'
                        ? '\u25CF\u25CF\u25CF\u25CF\u25CF\u25CF****'
                        : 'Sin clave'}
                  </code>
                  {prov.keySource !== 'env' && (
                    <button
                      onClick={() => onEditKey(prov.id)}
                      style={{
                        padding: '3px 6px',
                        background: '#f3f4f6',
                        color: '#374151',
                        border: 'none',
                        borderRadius: 4,
                        fontSize: 9,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                      }}
                    >
                      {prov.keySource === 'bd' ? 'Editar' : 'Configurar'}
                    </button>
                  )}
                  {prov.hasApiKey && (
                    <button
                      onClick={() => onTestKey(prov.id)}
                      disabled={prov.testing}
                      style={{
                        padding: '3px 6px',
                        background: '#2F5496',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 4,
                        fontSize: 9,
                        cursor: prov.testing ? 'default' : 'pointer',
                        fontFamily: 'inherit',
                        opacity: prov.testing ? 0.6 : 1,
                      }}
                    >
                      {prov.testing ? '...' : 'Probar'}
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Test result badge */}
            {prov.testResult === 'ok' && (
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: '#15803D',
                  background: '#DCFCE7',
                  borderRadius: 10,
                  padding: '2px 8px',
                }}
              >
                OK
              </span>
            )}
            {prov.testResult === 'error' && (
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: '#DC2626',
                  background: '#FEE2E2',
                  borderRadius: 10,
                  padding: '2px 8px',
                }}
              >
                Error{prov.testMsg ? `: ${prov.testMsg}` : ''}
              </span>
            )}

            {/* Active/inactive toggle */}
            {prov.hasApiKey && (
              <div style={{ marginTop: 8 }}>
                <button
                  onClick={() => onToggleProviderActive(prov)}
                  style={{
                    padding: '3px 8px',
                    fontSize: 10,
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    border: 'none',
                    borderRadius: 4,
                    background: prov.activo ? '#FEE2E2' : '#DCFCE7',
                    color: prov.activo ? '#DC2626' : '#15803D',
                  }}
                >
                  {prov.activo ? 'Desactivar' : 'Activar'}
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* New provider form */}
      {showNewProvider ? (
        <div
          style={{
            marginTop: 14,
            border: `1px dashed ${C.grisMedio}`,
            borderRadius: 8,
            padding: 14,
            display: 'flex',
            gap: 10,
            alignItems: 'flex-end',
            flexWrap: 'wrap',
          }}
        >
          <div>
            <label style={{ ...LABEL_STYLE, marginBottom: 2 }}>Nombre</label>
            <input
              type="text"
              style={{ ...INPUT_STYLE, width: 160, fontSize: 12, padding: '6px 10px' }}
              value={newProvider.nombre}
              onChange={(e) => onSetNewProvider((p) => ({ ...p, nombre: e.target.value }))}
            />
          </div>
          <div>
            <label style={{ ...LABEL_STYLE, marginBottom: 2 }}>Tipo</label>
            <select
              style={{
                ...INPUT_STYLE,
                width: 130,
                fontSize: 12,
                padding: '6px 10px',
                cursor: 'pointer',
              }}
              value={newProvider.tipo}
              onChange={(e) => onSetNewProvider((p) => ({ ...p, tipo: e.target.value }))}
            >
              <option value="Principal">Principal</option>
              <option value="Fallback">Fallback</option>
              <option value="Reserva">Reserva</option>
              <option value="Embeddings">Embeddings</option>
            </select>
          </div>
          <div>
            <label style={{ ...LABEL_STYLE, marginBottom: 2 }}>API Key</label>
            <input
              type="text"
              style={{ ...INPUT_STYLE, width: 200, fontSize: 12, padding: '6px 10px' }}
              placeholder="sk-..."
              value={newProvider.apiKey}
              onChange={(e) => onSetNewProvider((p) => ({ ...p, apiKey: e.target.value }))}
            />
          </div>
          <div>
            <label style={{ ...LABEL_STYLE, marginBottom: 2 }}>baseUrl</label>
            <input
              type="text"
              style={{ ...INPUT_STYLE, width: 220, fontSize: 12, padding: '6px 10px' }}
              placeholder="https://api.proveedor.com/v1"
              value={newProvider.baseUrl ?? ''}
              onChange={(e) => onSetNewProvider((p) => ({ ...p, baseUrl: e.target.value }))}
            />
          </div>
          <div>
            <label style={{ ...LABEL_STYLE, marginBottom: 2 }}>Modelo</label>
            <input
              type="text"
              style={{ ...INPUT_STYLE, width: 160, fontSize: 12, padding: '6px 10px' }}
              placeholder="modelo predeterminado"
              value={newProvider.modeloPredeterminado ?? ''}
              onChange={(e) =>
                onSetNewProvider((p) => ({ ...p, modeloPredeterminado: e.target.value }))
              }
            />
          </div>
          <div>
            <label style={{ ...LABEL_STYLE, marginBottom: 2 }}>Prioridad</label>
            <input
              type="number"
              min={1}
              max={99}
              style={{ ...INPUT_STYLE, width: 80, fontSize: 12, padding: '6px 10px' }}
              placeholder="99"
              value={newProvider.prioridad ?? ''}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10);
                onSetNewProvider((p) => ({ ...p, prioridad: isNaN(v) ? undefined : v }));
              }}
            />
          </div>
          <button
            onClick={onCreateProvider}
            style={{
              padding: '6px 14px',
              background: '#0E243E',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              fontSize: 11,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Crear
          </button>
          <button
            onClick={() => onSetShowNewProvider(false)}
            style={{
              padding: '6px 14px',
              background: '#f3f4f6',
              color: '#6b7280',
              border: 'none',
              borderRadius: 6,
              fontSize: 11,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Cancelar
          </button>
        </div>
      ) : (
        <button
          onClick={() => onSetShowNewProvider(true)}
          style={{
            marginTop: 14,
            padding: '8px 16px',
            background: '#0E243E',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          + Anadir proveedor
        </button>
      )}
    </div>
  );
}
